/* eslint-disable camelcase */

import {
  ClickStream,
  CustomerAttributes,
  FeatureSelections,
  UserContext,
} from "../types";

const getVirtualValueMessage = (
  isGECardHolder: boolean,
  hasOffers: string,
  rewardAmount: number
) => {
  // Default to '!GE, no Offer'
  const virtualValueMessage = "NG_0_NO";
  if (isGECardHolder) {
    if (hasOffers) {
      // GE, reward dollars, Offer
      return `GE_${rewardAmount}_OFR`;
    }
    // GE, reward dollars, no Offer
    return `GE_${rewardAmount}_NO`;
  }
  if (hasOffers) {
    //! GE, no reward, Offer
    return "NG_0_OFR";
  }

  return virtualValueMessage;
};

type getMsgType = {
  GECardHolder: string;
  hasOffers: string | boolean;
  totalRewardValue: string;
};

const getMsg = ({
  GECardHolder,
  totalRewardValue = "0",
  hasOffers = "false",
}: getMsgType) => {
  const isGECardHolder: boolean = String(GECardHolder) === "true" || false;
  const rewardAmount = parseFloat(totalRewardValue);

  const virtualValueMessage = getVirtualValueMessage(
    isGECardHolder,
    hasOffers as string,
    rewardAmount
  );

  return {virtual_value_message: virtualValueMessage};
};

const getCustomerType = (userContext: UserContext) => {
  const {isLoggedInUser, isRecognizedUser} = userContext;
  return isLoggedInUser === "true" || isRecognizedUser === "true"
    ? "existing"
    : "new";
};

const getRecognitionStatus = (userContext: UserContext) => {
  const {isLoggedInUser, isRecognizedUser} = userContext;
  if (isLoggedInUser === "true") {
    return "authenticated";
  }
  if (isRecognizedUser === "true") {
    return "recognized";
  }
  return "unrecognized";
};

const personalizedPreferenceRegex =
  /^[AT|ON|GAP|GP|BR]*[US|CA|JP|UK|EU]*(DIV|CAT)PREF[_pref\d]*$/;

type PersonalizationKeysMapType = {
  [key: string]: string;
};

const personalizedKeysMap: PersonalizationKeysMapType = {
  CARDSTATUS: "cardholder_status",
  DS: "discount_sensitivity",
  EMAILUS: "email_status",
};

const getPersonalizeKey = (key: string) =>
  key.match(personalizedPreferenceRegex)
    ? key.replace(personalizedPreferenceRegex, (match: string, p1: string) =>
        p1 === "DIV" ? "division_preference" : "category_preference"
      )
    : personalizedKeysMap[key];

type AttributeType = {
  [key: string]: string;
};

const extractCustomerInfo = (personalizedAttributes = []) => {
  const attributes =
    personalizedAttributes && Array.isArray(personalizedAttributes)
      ? personalizedAttributes
      : [personalizedAttributes];
  return attributes.reduce((data: AttributeType, attr: AttributeType) => {
    const key = getPersonalizeKey(attr.AttributeName);
    if (key) {
      const existingValue = data[key] || "";
      /* eslint-disable-next-line */
      data[key] = existingValue
        ? `${existingValue},${attr.AttributeValue}`
        : attr.AttributeValue;
    }
    return data;
  }, {});
};

type PersonalizationDataType = {
  featureSelections?: {
    cardholder_status?: string;
    division_preference?: string;
    discount_sensitivity?: string;
    category_preference?: string;
    email_status?: string;
    Evergreens?: [];
  };
  userContext?: UserContext;
  customerAttributes?: CustomerAttributes;
};

const mapPersonalizationData = (
  personalizationData: PersonalizationDataType = {}
) => {
  const {
    featureSelections = {} as FeatureSelections,
    userContext = {} as UserContext,
    customerAttributes = {} as CustomerAttributes,
  } = personalizationData;
  const {
    cardholder_status,
    division_preference,
    discount_sensitivity,
    category_preference,
    email_status,
  }: PersonalizationDataType["featureSelections"] = extractCustomerInfo(
    ((featureSelections as FeatureSelections) || {}).Evergreens
  );

  const {
    localeCode = "",
    preferredLocaleCode = "",
    userEmailEncrypt: encrypted_customer_email = "",
    userEmailMMEncrypt: encrypted_customer_email_mm = "",
    billingAddressEncrypt: encrypted_billing_address = "",
    clickStream = {},
    globalShippingCountryCode = "",
    globalShippingCurrencyCode = "",
    userEmailEncrypt256 = "",
  } = userContext as UserContext;

  const languageCode = localeCode || preferredLocaleCode;
  const {cipher: encryption_cipher = "", vector: encryption_vector = ""} =
    clickStream as ClickStream;
  const {virtual_value_message} = getMsg(
    (customerAttributes as CustomerAttributes) || {}
  );
  const customerType = getCustomerType(userContext as UserContext);
  const recognitionStatus = getRecognitionStatus(userContext as UserContext);

  return {
    currency_code: globalShippingCurrencyCode,
    country_code: globalShippingCountryCode,
    shipping_country_code: globalShippingCountryCode,
    cardholder_status,
    division_preference,
    discount_sensitivity,
    category_preference,
    email_status,
    encrypted_customer_email,
    encrypted_customer_email_mm,
    encrypted_billing_address,
    hashed_customer_email: userEmailEncrypt256,
    encryption_cipher,
    encryption_vector,
    language_code: languageCode,
    virtual_value_message,
    customer_type: customerType,
    recognition_status: recognitionStatus,
    mtl_member_status:
      (customerAttributes as CustomerAttributes).mtlMember || "",
    tier_status: (customerAttributes as CustomerAttributes).tier || "",
  };
};
// this is for Personalization Team
// this allows Tealium extensions to access Personalization data to generate badges

declare global {
  interface Window {
    personalizationData: PersonalizationDataType;
  }
}

const exposeDataToWindow = (data: PersonalizationDataType) => {
  window.personalizationData = data;
  return data;
};
export type DataLayer = {
  add: (config: {
    name: string;
    priority?: number;
    data: PersonalizationDataType;
  }) => void;
  builderNames(): string;

  isTealiumReady(): void;
  view(): Promise<void>;
};
export default ({datalayer, dataPromise}: any) => {
  datalayer.add({
    name: "personalization",
    data: dataPromise
      .then(exposeDataToWindow)
      .then(mapPersonalizationData)
      .catch((e: {message: any}) => ({
        error_message: `[Personalization Layer] error building: ${e.message}`,
      })),
  });
};
